import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useReducer,
  useRef,
  ReactWithChildren,
} from 'react'
import * as Phoenix from 'phoenix'
import * as R from 'ramda'
import { OrderAPI5Ctx, useOrdersPresence } from '../views/order/v5/api/order-state'

import * as ordersApi from 'api/orders'
import { conditionsToQuery } from 'utils/conditionsToQuery'

import * as userStore from 'stores/user'
import { GET_ORDER_CARD, OrderState } from 'views/order/v5/model-order'

import client from 'api/auth'

// LIVE LIST CTX
// ****************************************************************
interface LiveAPI {
  lists: LiveLists
}

interface OrderInfo {
  uuid: string
  node_id: string
  node_name: string
  card_type: string
  channel_id: number
  customer_name: string
  order_type: string
  order_at: string
  restaurant_id: number
  short_uuid: string
  state: OrderState
  total: number
  payment_type: string
  workflow_id: number
  workflow_node_id: string
}

interface LiveList {
  id: string
  name: string
  isSupport: boolean
  restaurantIds: number[]
  live: boolean
  loadingOrders: boolean
  channels: Phoenix.Channel[]
  orders: Partial<OrderInfo>[]
  hasMore: boolean
  hasChanges: boolean
  lastUpdate: string | null
}

type LiveListAction =
  | { id: 'SET_LISTS'; lists: LiveLists }
  | { id: 'LOAD_ORDERS'; channelId: string }
  | { id: 'SET_ORDERS'; channelId: string; orders: OrderInfo[]; hasMore: boolean }
  | { id: 'UPDATE_CONNECT_STATE'; channelId: string; connect: boolean }
  | { id: 'UPDATE_ORDER_NODE'; channelId: string; nodeInfo: NodeInfo }
  | { id: 'UPDATE_TIME_STATE'; channelId: string; timeInfo: TimeInfo }
  | { id: 'UPDATE_ORDER_STATE'; channelId: string; stateInfo: StateInfo }
  | { id: 'SOUND_PLAYED'; when: Date }

const LiveCtx = createContext<LiveAPI | null>(null)

interface NodeInfo {
  uuid: string
  node: string
  node_name: string
  transitions: NodeTransition[]
}

interface NodeTransition {
  category: string
  from_node: string
  label: null | string
  name: string
  roles: string[]
  time: null | string
  time_constraints: null | string
  to_node: string
  transition_type: string
}

interface StateInfo {
  uuid: string
  state: OrderState
}

interface TimeInfo {
  uuid: string
  order_at: string
}

// type LiveLists = LiveList[]
interface LiveLists {
  [id: string]: LiveList
}

interface OrderSearchResult {
  page: number
  per_page: number
  results: OrderInfo[]
  total: { value: number }
}

declare const ACCESS_GROUPS_SVC: string

interface AccessGroupInfo {
  id: number
  name: string
  description: string
  parent_id: number | null
}

const authJsonHeaders = (token: string): Record<string, string> => {
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  }
}
function loadAccessGroups(role: string): Promise<{ data: AccessGroupInfo[] }> {
  const url = `${ACCESS_GROUPS_SVC}/search`
  const token = client.getAuthResponse().access_token
  return fetch(url, {
    method: 'POST',
    headers: authJsonHeaders(token),
    body: JSON.stringify({ ag_role: role }),
  }).then((res) => res.json())
}

function loadOrders(list: LiveList): Promise<OrderSearchResult> {
  const includedStates = ['missed']
  let conditions: { field: string; op: string; value: any }[] = [
    {
      field: 'order_at',
      op: 'within',
      value: {
        count: 24,
        unit: 'hour',
      },
    },
  ]

  if (!list.isSupport) {
    includedStates.push('executing')
  }

  conditions = includedStates.reduce(
    (conditions: any, state: string) => [...conditions, { field: 'state', op: 'equal', value: state }],
    conditions
  )

  const conditionsObj = conditionsToQuery(conditions, userStore.timezone())
  return ordersApi.searchRaw('all', 'order_at', 'asc', 1, conditionsObj, 100)
}

function orderAccepted(o: Partial<OrderInfo>, list: LiveList): boolean {
  if (list.isSupport) {
    return o.state == 'missed'
  } else {
    return o.state == 'missed' || o.state == 'executing'
  }
}

function useInterval(callback: () => void, delay: number): void {
  const savedCallback = useRef<() => void>()

  useEffect(() => {
    savedCallback.current = callback
  }, [callback])

  useEffect(() => {
    function tick() {
      if (savedCallback.current) {
        savedCallback.current()
      }
    }
    if (delay !== null && delay !== undefined) {
      const id = setInterval(tick, delay)
      return () => clearInterval(id)
    }
  }, [delay])
}

export function LiveLists(props: ReactWithChildren<{}>): JSX.Element {
  const [accessGroups, setAccessGroups] = useState<AccessGroupInfo[]>([])
  useEffect(() => {
    Promise.all([loadAccessGroups('restaurants'), loadAccessGroups('channels')]).then(
      ([rag, cag]: { data: AccessGroupInfo[] }[]): void => {
        setAccessGroups(R.uniqBy(R.prop('id'), [...rag.data, ...cag.data]))
      }
    )
  }, [])

  if (accessGroups.length == 0) {
    return <></>
  }
  return <AGLiveLists accessGroups={accessGroups}>{props.children}</AGLiveLists>
}

export function AGLiveLists(props: ReactWithChildren<{ accessGroups: AccessGroupInfo[] }>): JSX.Element {
  const socket = useContext(OrderAPI5Ctx) as Phoenix.Socket
  const [listsState, dispatch] = useReducer(listManager, {})
  const lists = listsState //useDebounce(listsState, 1000)
  const [loggedIn, setLoggedIn] = useState(false)
  const shouldPlaySound = useDebounce(
    R.values(listsState).reduce((r: boolean, l: LiveList) => r || l.hasChanges, false),
    3000
  )

  // const isSupport = userStore.hasAnyRole('support', 'restaurant_admin', 'ambassador')
  // const isZupplerSupport = userStore.hasRole('support')
  // const restaurants = userStore.acls().restaurant || []

  useInterval(
    () =>
      R.values(listsState).forEach((list: LiveList) => {
        if (!list.live) {
          loadOrders(list).then((r: OrderSearchResult) =>
            dispatch({
              id: 'SET_ORDERS',
              channelId: list.id,
              orders: r.results,
              hasMore: r.results.length > 0,
            })
          )
        }
      }),
    1000 * 60
  )

  useEffect(() => {
    const s: null | HTMLAudioElement = document.getElementById('noise-service-notification') as HTMLAudioElement
    if (s && shouldPlaySound) {
      s.play()
        .then(() => dispatch({ id: 'SOUND_PLAYED', when: new Date() }))
        .catch((reason) => console.log('Could not autoplay sound', reason))
    }
  }, [shouldPlaySound])

  function updateList(state: LiveLists, listId: string, updater: (list: LiveList) => LiveList | null): LiveLists {
    const list = state[listId]
    if (list) {
      const newList = updater(list)
      if (newList) {
        newList.lastUpdate = new Date().toString()
        state[listId] = newList
      }
    } else {
      console.warn('Unknown list with id ', listId)
    }
    return { ...state }
  }

  function listManager(state: LiveLists, action: LiveListAction): LiveLists {
    // console.log('List Manager processing action', action, state)
    // console.time('ListManager')
    let newState: LiveLists = state
    switch (action.id) {
      case 'SET_LISTS':
        newState = action.lists
        break
      case 'UPDATE_CONNECT_STATE':
        newState = updateList(state, action.channelId, (l: LiveList) => {
          if (action.connect) {
            l.live = action.connect
            loadOrders(l).then((r: OrderSearchResult) => {
              dispatch({
                id: 'SET_ORDERS',
                channelId: l.id,
                orders: r.results,
                hasMore: r.results.length > 0,
              })
            })
            return l
          } else {
            return null
          }
        })
        break
      case 'SET_ORDERS':
        newState = updateList(state, action.channelId, (l: LiveList) => {
          l.hasMore = action.hasMore
          l.orders = action.orders
          return l
        })
        break
      case 'UPDATE_ORDER_NODE': {
        newState = updateList(state, action.channelId, (l: LiveList) => {
          const orderIndex = l.orders.findIndex(R.propEq('uuid', action.nodeInfo.uuid))
          const order = l.orders[orderIndex]
          if (order) {
            order.node_id = action.nodeInfo.node
            order.node_name = action.nodeInfo.node_name
            l.orders.splice(orderIndex, 1).unshift(order)
            return l
          } else {
            return null
          }
        })
        break
      }
      case 'UPDATE_TIME_STATE': {
        newState = state
        // TODO: Ignore this here... as it is not needed to update due time on listing card
        break
      }
      case 'UPDATE_ORDER_STATE':
        newState = updateList(state, action.channelId, (l: LiveList) => {
          const orderIndex = l.orders.findIndex(R.propEq('uuid', action.stateInfo.uuid))
          if (orderIndex >= 0) {
            const order = l.orders[orderIndex]
            order.state = action.stateInfo.state
            if (orderAccepted(order, l)) {
              l.hasChanges = true
              l.orders.splice(orderIndex, 1).unshift(order)
            } else {
              l.orders.splice(orderIndex, 1)
            }
            return l
          } else {
            const order = { uuid: action.stateInfo.uuid, state: action.stateInfo.state }
            if (orderAccepted(order, l)) {
              l.orders.unshift(order)
              l.hasChanges = true
              return l
            } else return null
          }
        })
      case 'SOUND_PLAYED':
        R.values(newState).forEach((l) => ({ ...l, hasChanges: false, lastUpdate: new Date().toString() }))
        break
      default:
        newState = state
    }
    // console.timeEnd('ListManager')
    return newState
  }

  useEffect(() => userStore.loggedIn.onValue(setLoggedIn), [])

  useEffect(() => {
    if (loggedIn) {
      let channelNames = []
      channelNames = props.accessGroups.map((ag: AccessGroupInfo) => ({
        id: `ag-${ag.id}`, //ag.parent_id ? ag.name : 'Live Orders',
        restaurantIds: [],
        channelIds: [`access_group:${ag.id}`],
        name: ag.name,
        isSupport: ag.parent_id == null,
      }))

      const listsFinal = channelNames
        .map(
          ({
            id,
            channelIds,
            restaurantIds,
            name,
            isSupport,
          }: {
            id: string
            channelIds: string[]
            restaurantIds: number[]
            name: string
            isSupport: boolean
          }): LiveList => {
            const listId = id
            const channels = channelIds.map((channelId: string) => {
              console.log('Connecting to channel', channelId, name)
              const channel: Phoenix.Channel = socket.channel(channelId)

              // Todo make lists as a reducer and send message to update state
              channel.onError((reason?: any) => console.error(`Error on channel ${name}`, reason))
              channel.onClose(() => dispatch({ id: 'UPDATE_CONNECT_STATE', channelId: listId, connect: false }))

              channel.on('node_changed', (params: NodeInfo) => {
                console.log('node_changed', channelId, listId, name, params)
                dispatch({ id: 'UPDATE_ORDER_NODE', channelId: listId, nodeInfo: params })
              })
              channel.on('order_state_changed', (params: StateInfo) => {
                console.log('order_state_changed', channelId, listId, name, params)
                dispatch({ id: 'UPDATE_ORDER_STATE', channelId: listId, stateInfo: params })
              })
              channel.on('order_time_changed', (params: TimeInfo) => {
                console.log('order_time_changed', channelId, listId, name, params)
                dispatch({ id: 'UPDATE_TIME_STATE', channelId: listId, timeInfo: params })
              })

              channel
                .join()
                .receive('ok', (response?: any) => {
                  console.log('Connected to', listId, channelId)
                  dispatch({ id: 'UPDATE_CONNECT_STATE', channelId: listId, connect: true })
                })
                .receive('error', (response?: any) => {
                  console.error(`Error on channel ${name}`, response)
                  channel.leave()
                  dispatch({ id: 'UPDATE_CONNECT_STATE', channelId: listId, connect: false })
                })
                .receive('timeout', () => console.log(`Networking issue. Still waiting... on ${channelId} ${name}`))
              return channel
            })

            return {
              id: id,
              name: name,
              restaurantIds: restaurantIds,
              channels,
              live: false,
              orders: [],
              isSupport,
              loadingOrders: false,
              hasChanges: false,
              hasMore: false,
              lastUpdate: new Date().toString(),
            }
          },
          {}
        )
        .reduce((all: LiveLists, l: LiveList) => R.assoc(l.id, l, all), {})

      dispatch({
        id: 'SET_LISTS',
        lists: listsFinal,
      })
    }
    return () => R.values(listsState).forEach(({ channels }) => channels.forEach((c) => c.leave()))
  }, [loggedIn])

  return <LiveCtx.Provider value={{ lists: lists }}>{props.children}</LiveCtx.Provider>
}

export function useLiveLists(): LiveAPI {
  const ctx = useContext(LiveCtx) as LiveAPI
  return ctx
}

import { Icon } from 'react-fa'
import { Nav, NavItem, Label, NavLink, Button } from 'reactstrap'
import cx from 'classnames'
import { OrdersAPI5ClientCtx, RestaurantsAPI5ClientCtxx } from 'context'
import { useQuery, useLazyQuery } from '@apollo/react-hooks'
import { GET_RESTAURANT_INFO, GET_RESTAURANT_WORKFLOW } from 'views/order/v5/model-restaurant'

export function LiveListsSidebar(props: { expanded: boolean }): JSX.Element {
  const { lists } = useLiveLists()
  const expandedClass = cx('text-justify', { hidden: !props.expanded })
  return (
    <div>
      <div className={expandedClass}>
        <div className="list-title">
          <span>Live Orders</span>
        </div>
      </div>
      <Nav pills vertical>
        {R.values(lists).map((l, index) => (
          <LiveListItem key={(l.lastUpdate || l.id) + index} list={l} expanded={props.expanded} />
        ))}
      </Nav>
    </div>
  )
}

function LiveListItem(props: { list: LiveList; expanded: boolean }): JSX.Element {
  const l = props.list

  const restaurantClient = useContext(RestaurantsAPI5ClientCtxx)

  if (!restaurantClient) {
    return <></>
  }

  let restaurantQuery = null
  if (l.restaurantIds && l.restaurantIds.length == 1) {
    restaurantQuery = useQuery(GET_RESTAURANT_INFO, {
      variables: {
        restaurant_id: l.restaurantIds[0],
      },
      client: restaurantClient,
    })

    if (restaurantQuery && !restaurantQuery.data) {
      return <></>
    }
  }

  const listName = restaurantQuery && restaurantQuery.data ? restaurantQuery.data.restaurant.name : l.name

  if (props.expanded) {
    return (
      <NavItem key={l.id} active={l.live}>
        <div style={{ overflowX: 'hidden' }}>
          <span className="pull-right">
            <Label key={l.lastUpdate} color="danger" className="pull-right">
              {l.orders.length}
              {l.hasMore ? '+' : ''}
            </Label>
          </span>
          <NavLink href={`#/live/${l.id}`}>
            <Icon name={l.live ? (l.hasChanges ? 'bell' : 'play') : 'pause'} /> {listName}
          </NavLink>
        </div>
      </NavItem>
    )
  } else {
    return <span>{listName}</span>
  }
}

import { useRouteMatch, Route } from 'react-router'
import { Container, Row, Col } from 'reactstrap'

export function LiveListCards(): JSX.Element {
  const match = useRouteMatch('/live/:listId/:orders(orders)?/:orderId?')
  const showCurrentOrder = !!match.params.orderId

  const { lists } = useLiveLists()
  const list = R.values(lists).find((l: LiveList) => String(l.id) == match.params.listId)

  if (!list) {
    return <>Unknown list</>
  } else {
    return (
      <Container fluid>
        <Row>
          <Col lg={showCurrentOrder ? 3 : 12} xs={12}>
            <LiveListOrders
              key={list.lastUpdate || list.id}
              listId={list.id}
              list={list}
              isSupport={list.isSupport}
              orders={list.orders}
              expand={!showCurrentOrder}
              restaurantIds={list.restaurantIds}
            />
          </Col>
          <Route exact path="/live/:listId/orders/:orderId" component={OrderPage} />
        </Row>
      </Container>
    )
  }
}

import { OrderPage } from 'views/order/selector'

function LiveListOrders(props: {
  listId: string
  isSupport: boolean
  list: LiveList
  orders: Partial<OrderInfo>[]
  expand: boolean
  restaurantIds: number[]
}) {
  const restaurantClient = useContext(RestaurantsAPI5ClientCtxx)

  if (!restaurantClient) {
    return <></>
  }

  let restaurantQuery = null
  if (props.restaurantIds && props.restaurantIds.length == 1) {
    restaurantQuery = useQuery(GET_RESTAURANT_INFO, {
      variables: {
        restaurant_id: props.restaurantIds[0],
      },
      client: restaurantClient,
    })

    if (restaurantQuery && !restaurantQuery.data) {
      return <></>
    }
  }

  const listName = restaurantQuery && restaurantQuery.data ? restaurantQuery.data.restaurant.name : props.list.name

  return (
    <>
      {props.expand && (
        <>
          <h3>{listName} Live Orders:</h3>
          <p className="info">
            This section initially shows all orders due within next 3 hours that requires your attention and then it
            pushed all new orders to the top as soon as their status changes. Orders with <em>Customer Connect</em>{' '}
            enabled will display their status on the card header.
          </p>
        </>
      )}
      <div className={props.expand ? 'cards multiple' : ''}>
        {props.orders.map((o: OrderInfo) => (
          <OrderCard key={o.uuid} order={o} isSupport={props.isSupport} />
        ))}
      </div>
    </>
  )
}

import { Card, CardBody, CardHeader, CardFooter, CardColumns, CardLink, CardGroup } from 'reactstrap'
import { ApolloClient } from 'apollo-boost'

function stateToIcon(state: string): string {
  switch (state) {
    case 'confirmed':
      return 'check'
    case 'invoiced':
      return 'check-circle-o'
    case 'missed':
      return 'exclamation'
    case 'canceled':
      return 'remove'
    case 'editing':
      return 'pencil-square-o'
    case 'captured':
      return 'plus'
    case 'executing':
      return 'shopping-cart'
    case 'created':
      return 'plus-square-o'
    case 'rejected':
      return 'ban'
    case 'prepared':
      return 'check'
    default:
      return 'info'
  }
}

function paymentTypeToIcon(pt: string): string {
  switch (pt) {
    case 'CASH':
    case 'CARD_ON_DELIVERY':
    case 'COD':
      return 'money'
    case 'CARD':
    case 'LEVELUP':
      return 'credit-card'
    case 'BUCKID':
    case 'ACCOUNT':
      return 'google-wallet'
    case 'POINTS':
      return 'dot-circle-o'
    case 'PAYPAL':
      return 'paypal'
    case 'MOLLIE':
      return 'cc-paypal'
    default:
      return 'question-mark'
  }
}

import moment from 'moment'
import momentTz from 'moment-timezone'
import { PresenceInfo as UserPresence5 } from '../views/order/v5/fragments/info'

import presenceStore from 'stores/presence'

function useOldPresence(orderUUID: string) {
  const [presenceInfo, setPresenceInfo] = useState<string[]>([])
  useEffect(() => {
    return presenceStore.orders.map((orders: any) => orders[orderUUID]).onValue(setPresenceInfo)
  }, [orderUUID])
  return presenceInfo || []
}

function UserPresence(props: { orderId: string }) {
  const users = useOldPresence(props.orderId)

  const initials = R.compose(
    R.join(''),
    R.map((str) => str.substring(0, 1)),
    R.split(/\s+/)
  )
  const roles = R.join(', ')
  const roleToClass = R.map((n) => `presence-role-${n}`)

  const usersTags = users.map((u: any) => {
    const className = cx('label', 'label-default', roleToClass(u.roles).join(' '))
    const title = `${u.user} is ${roles(u.roles)}`
    return (
      <span key={u.email} className={className} title={title}>
        {initials(u.user) || 'N/A'}
      </span>
    )
  })

  return <span className="presence-info">{usersTags}</span>
}

function OrderCard(props: { order: OrderInfo; isSupport: boolean }): JSX.Element {
  const match = useRouteMatch<{ listId: string; orderId: null | string }>([
    '/live/:listId',
    '/live/:listId/orders/:orderId',
  ])
  const ordersClient = useContext(OrdersAPI5ClientCtx)

  const presence = useOrdersPresence()
  const orderPresence = R.defaultTo([], R.path([props.order.uuid, 'metas'], presence))

  if (!ordersClient || !match) {
    return <></>
  }

  const [loadOrder, { data: orderData }] = useLazyQuery(GET_ORDER_CARD, {
    variables: { id: props.order.uuid },
    client: ordersClient,
    errorPolicy: 'all',
  })

  useEffect(() => {
    if (!props.order.restaurant_id || !props.order.customer_name || !props.order.order_type) {
      loadOrder()
    }
  }, [props.order.uuid, props.order.restaurant_id, props.order.customer_name, props.order.order_type])

  const restaurantId = props.order.restaurant_id || R.path(['order', 'carts', 'restaurant_id'], orderData)
  const customerName = props.order.customer_name || R.path(['order', 'carts', 'customer', 'name'], orderData)
  const orderType = props.order.order_type || R.path(['order', 'carts', 'settings', 'service', 'label'], orderData)
  const timeValue = props.order.order_at || R.path(['order', 'due_time'], orderData)
  const shortUUID = props.order.uuid.split(/-/)[0]
  const cardType = props.order.payment_type || R.path(['order', 'carts', 'settings', 'tender', 'id'], orderData) || ''
  const orderTotal = props.order.total || R.path(['order', 'totals', 'total'], orderData) || 0
  const workflowId = props.order.workflow_id || R.path(['order', 'workflow_id'], orderData) || null
  const nodeId = props.order.workflow_node_id || R.path(['order', 'workflow_node_id'], orderData) || null

  const timeZonetime = momentTz.utc(timeValue).format()
  let timeInfo = moment(timeZonetime).fromNow()
  if (timeInfo == 'Invalid Date') {
    timeInfo = ''
  }

  if (!restaurantId) return <>Loading order {props.order.uuid}</>

  return (
    <Card
      key={props.order.uuid}
      className={orderStateClass(props.order.state, timeValue || '', match.params.orderId === props.order.uuid)}>
      <CardLink href={`#/live/${match.params.listId}/orders/${props.order.uuid}`}>
        <CardHeader>
          <div>
            <Icon name={stateToIcon(props.order.state)} title={props.order.state} />{' '}
            {props.isSupport && restaurantId && <RestaurantName restaurantId={restaurantId} />}
            <WorkflowNodeInfo
              restaurantId={restaurantId}
              workflowId={workflowId}
              nodeId={nodeId}
              nodeName={props.order.node_name}
            />
          </div>
          <UserPresence5 presence={orderPresence || []} />
          <UserPresence orderId={props.order.uuid} />
        </CardHeader>
        <CardBody>
          <span key="name" className="customer-name">
            {customerName}
          </span>
          <span key="sid" className="order-type">
            {orderType}
          </span>
        </CardBody>
        <CardFooter>
          <span key="p">
            <Icon name={paymentTypeToIcon(cardType)} /> {(orderTotal / 100).toFixed(0)}
          </span>
          <span key="c">
            <Icon name="clock-o" /> {timeInfo}
          </span>
          <span key="t">
            <Icon name="ticket" /> {shortUUID}
          </span>
        </CardFooter>
      </CardLink>
    </Card>
  )
}

function RestaurantName(props: { restaurantId: number }) {
  const restaurantClient = useContext(RestaurantsAPI5ClientCtxx)
  const [loadRestaurantInfo, { data: restaurantData }] = useLazyQuery(GET_RESTAURANT_INFO, {
    variables: { restaurant_id: props.restaurantId },
    client: restaurantClient,
  })

  useEffect(() => {
    if (props.restaurantId) {
      loadRestaurantInfo()
    }
  }, [props.restaurantId])

  const restaurantName = restaurantData && restaurantData.restaurant ? restaurantData.restaurant.name : '...'
  return <span>{restaurantName}: </span>
}

import { timeSlice } from 'utils/time'
import useDebounce from 'utils/use-debounce'
import { Pagination } from 'views/invoices/model'
function orderStateClass(state: string, timeValue: string, active: boolean) {
  const time = momentTz.tz(timeValue, userStore.timezone())
  return cx(`panel-${state}-${timeSlice(time)}`, { active })
}

function WorkflowNodeInfo(props: {
  restaurantId: number
  workflowId: number
  nodeId?: string
  nodeName: null | string
}) {
  const { restaurantId, workflowId, nodeId } = props
  const restaurantClient = useContext(RestaurantsAPI5ClientCtxx)

  const [loadWorkflow, { data }] = useLazyQuery(GET_RESTAURANT_WORKFLOW, {
    variables: { restaurant_id: restaurantId, workflow_id: workflowId },
    client: restaurantClient,
  })

  useEffect(() => {
    if (restaurantId && workflowId && !props.nodeName) {
      loadWorkflow()
    }
  }, [restaurantId, workflowId])

  if (props.nodeName) {
    return <span className="badge">{props.nodeName}</span>
  } else if (data && data.restaurant && data.restaurant.workflow && data.restaurant.workflow.content) {
    const nodes = data.restaurant.workflow.content.nodes
    const node = nodes.find(R.propEq('id', nodeId))
    if (node) {
      return <span className="">{node.name || 'n/a'}</span>
    } else {
      return <span className="">{nodeId}</span>
    }
  } else {
    return <></>
  }
}
